/**
 * The session: a Supabase JWT, the organization it is scoped to, and nothing
 * else.
 *
 * **Supabase Auth is an identity provider only** (ED-06, S7 §25.7). It answers
 * "who is this?" and the API owns every authorization decision. So this module
 * exchanges credentials for a token and attaches it to requests; it makes no
 * claim about what the holder may do, and there is no place here to cache one.
 *
 * **No SDK.** Four HTTP calls against a documented REST endpoint, for the same
 * reason `ResendEmailAdapter` is one call and not a vendor package: an SDK is a
 * dependency, a vendor import inside `apps/`, and a second place the retry and
 * refresh policy would live.
 *
 * **The access token stays in memory; only the refresh token is persisted.**
 * That is a real trade and it is written down rather than assumed. An access
 * token in `localStorage` is readable by any script that gets into the page;
 * in memory it dies with the tab. The refresh token has to survive a reload or
 * every refresh signs the user out, so it is persisted — and it is the one
 * credential whose theft the server can revoke. The stronger arrangement is an
 * httpOnly cookie issued by our own auth proxy, which is a backend this
 * product does not have; it is recorded in `docs/open-decisions.md`.
 */
import qrcode from 'qrcode-generator';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

/** Where the refresh token lives across reloads. Namespaced so a second app on
 *  the same origin cannot read it by guessing `token`. */
const REFRESH_KEY = 'premisesense.refresh';
/** The chosen organization. Not a credential — the API re-checks membership on
 *  every request — so it is safe to persist and rude not to. */
const ORG_KEY = 'premisesense.organization';

export type Session = {
  accessToken: string;
  expiresAt: number;
  email: string;
};

export class AuthError extends Error {
  constructor(
    message: string,
    readonly code: string,
    /** The provider's HTTP status, or 0 when there was no response. */
    readonly status = 0,
  ) {
    super(message);
  }
}

let session: Session | null = null;
let refreshing: Promise<Session | null> | null = null;

function configured(): boolean {
  return Boolean(SUPABASE_URL && PUBLISHABLE_KEY);
}

/**
 * Turn a provider error into something a person can act on.
 *
 * Supabase returns `invalid_grant` for a wrong password, a wrong email, and an
 * unconfirmed address alike — which is correct, because distinguishing them
 * tells an attacker which addresses exist. The message says so rather than
 * inventing a more specific one.
 */
function authMessage(code: string, status: number): string {
  if (status === 400 || code === 'invalid_grant')
    return 'That email and password did not match an account. If you have just been invited, use the link in your invitation instead.';
  if (status === 429) return 'Too many attempts. Wait a minute and try again.';
  return 'Sign-in is unavailable right now. This is not a problem with your password.';
}

async function auth<T>(path: string, body: unknown): Promise<T> {
  if (!configured())
    throw new AuthError(
      'Sign-in is not configured in this build. VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY are read at build time, so setting them after a build has no effect.',
      'not_configured',
    );

  const res = await fetch(`${SUPABASE_URL}/auth/v1/${path}`, {
    method: 'POST',
    headers: { apikey: PUBLISHABLE_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const code = String(detail.error_code ?? detail.error ?? res.status);
    throw new AuthError(authMessage(code, res.status), code, res.status);
  }
  return res.json() as Promise<T>;
}

type TokenResponse = {
  access_token: string;
  refresh_token: string;
  /** Optional: some provider responses omit it. See `adopt`. */
  expires_in?: number;
  user?: { email?: string };
};

function adopt(token: TokenResponse): Session {
  session = {
    accessToken: token.access_token,
    // A minute of margin. Refreshing at the exact expiry races the clock skew
    // between this browser and the provider, and the failure mode is a 401 on
    // a request the user did make.
    //
    // The fallback is not defensive padding. `expires_in` is absent from some
    // of these responses, and `undefined - 60` is NaN — which makes
    // `expiresAt > Date.now()` false forever, so every single request refreshes
    // the token first. That is invisible when it works and catastrophic after
    // the second factor: a refresh can hand back a session at aal1, silently
    // undoing the verification the user just completed, and the only symptom
    // is a 403 that says nothing.
    expiresAt: Date.now() + ((token.expires_in ?? 3600) - 60) * 1000,
    email: token.user?.email ?? '',
  };
  try {
    localStorage.setItem(REFRESH_KEY, token.refresh_token);
  } catch {
    // A browser with storage blocked still gets a working session; it just
    // ends when the tab does. Failing the sign-in over it would be worse.
  }
  return session;
}

export async function signIn(email: string, password: string): Promise<Session> {
  return adopt(await auth<TokenResponse>('token?grant_type=password', { email, password }));
}

/**
 * B5-08. Sends the reset mail. Never says whether the address exists.
 *
 * **The app says where the link should land, rather than the provider's
 * dashboard deciding.** Without `redirect_to`, Supabase builds the email from
 * its Site URL setting — which defaults to `http://localhost:3000`, so the
 * first real reset email pointed at the user's own machine. Worse, the setting
 * is one value for the whole project, and this product has three environments.
 *
 * `window.location.origin` means each environment sends people back to itself
 * with no per-environment build variable to forget, and a local development
 * server keeps working.
 *
 * The path matters as much as the origin. The recovery token arrives in the
 * URL fragment, and `/reset/complete` is the only screen that reads it — a
 * link to the site root drops the token at a screen that redirects to sign-in.
 *
 * Supabase requires the URL to match its Redirect URLs allow-list, which is a
 * dashboard setting per environment. `docs/setup/08-supabase-auth.md` covers
 * it.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const target = `${window.location.origin}/reset/complete`;
  await auth(`recover?redirect_to=${encodeURIComponent(target)}`, { email });
}

/** B5-08, the second half: the user arrived from the link and sets a password.
 *  The recovery token arrives in the URL fragment, which is why it is read
 *  from `location.hash` and never from a query string — a fragment is not sent
 *  to the server and does not reach a log or a referrer header. */
export async function completePasswordReset(accessToken: string, password: string): Promise<void> {
  if (!configured()) throw new AuthError('Sign-in is not configured.', 'not_configured');
  const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
    method: 'PUT',
    headers: {
      apikey: PUBLISHABLE_KEY,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password }),
  });
  if (!res.ok)
    throw new AuthError(
      'That reset link has expired or has already been used. Request a new one.',
      'reset_failed',
    );
}

/** The `email` claim of a token, for display only. The API reads its own. */
function emailOf(token: string): string {
  try {
    const [, encoded] = token.split('.');
    if (!encoded) return '';
    const payload = JSON.parse(atob(encoded.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.email === 'string' ? payload.email : '';
  } catch {
    return '';
  }
}

/** Whether this browser holds a session that survives a reload. */
export function hasStoredSession(): boolean {
  try {
    return Boolean(localStorage.getItem(REFRESH_KEY));
  } catch {
    return false;
  }
}

/** Set the password of the account this session belongs to. */
export async function setPassword(password: string): Promise<void> {
  if (!configured()) throw new AuthError('Sign-in is not configured.', 'not_configured');
  const token = session?.accessToken;
  if (!token)
    throw new AuthError('Your sign-in link has expired. Open the link again.', 'no_session');
  const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
    method: 'PUT',
    headers: {
      apikey: PUBLISHABLE_KEY,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const code = String(detail.error_code ?? detail.error ?? res.status);
    // Supabase refuses a password change on an account with two-factor
    // authentication unless this session has presented the second factor
    // (`insufficient_aal`, 401). A reset link proves only the mailbox, so an
    // Owner or Admin who forgot their password hit a generic "could not be
    // set" with nothing to act on (founder, 2026-09-17). The reset screen now
    // asks for the code first; this is the backstop if it ever does not.
    if (code === 'insufficient_aal')
      throw new AuthError(
        'This account uses two-factor authentication. Enter the six-digit code from your authenticator app, then set the password.',
        'second_factor_required',
        res.status,
      );
    throw new AuthError(
      res.status === 422
        ? 'That password was refused. Use at least 12 characters, and not one used on this account before.'
        : // The provider's code goes on the end: it names the cause without
          // naming the account, and it is what makes the next report fixable.
          `The password could not be set right now. This is not a problem with the password. (${code})`,
      'password_failed',
      res.status,
    );
  }
}

async function refresh(): Promise<Session | null> {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(REFRESH_KEY);
  } catch {
    stored = null;
  }
  if (!stored) return null;

  try {
    return adopt(
      await auth<TokenResponse>('token?grant_type=refresh_token', {
        refresh_token: stored,
      }),
    );
  } catch (e) {
    // Only a refusal ends the session. A refresh token the provider rejects
    // was revoked or has expired, and clearing it turns the next request into
    // a redirect to sign-in rather than a loop.
    //
    // A dropped connection, a provider outage or rate limiting is not that.
    // Clearing the token on any failure signed people out of a working
    // session because a phone lost signal for a moment — and, for an Owner
    // or Admin, sent them through the password and second factor again.
    if (e instanceof AuthError && e.status >= 400 && e.status < 500 && e.status !== 429) signOut();
    return null;
  }
}

/**
 * The token for the next request, refreshed if it is about to expire.
 *
 * Concurrent callers share one refresh. Without that, ten components mounting
 * at once produce ten refresh calls, nine of which invalidate the token the
 * tenth just stored — Supabase rotates refresh tokens on use.
 */
/**
 * The second factor.
 *
 * Owner and Admin are refused every request unless the session asserts
 * `aal2`, and until now the product had a screen that explained that and no
 * way to satisfy it — it told people to "complete the second factor with your
 * identity provider", which is an instruction nobody can follow, because
 * Supabase has no end-user interface for TOTP. It is an API, and this is the
 * half of it that was missing.
 *
 * The first account is the sharp case: an Owner who cannot enrol cannot use
 * the product, and cannot invite anybody who could.
 */

export type Factor = {
  id: string;
  status: 'verified' | 'unverified';
  factor_type: string;
  friendly_name?: string;
};

export type Enrolment = {
  factorId: string;
  /** The shared secret, in the form a person types into an authenticator. */
  secret: string;
  /** `otpauth://` — what the QR encodes. */
  uri: string;
  /** Something an `<img src>` can load. Rendered that way and never inlined as
   *  markup: it arrives over the network onto a page holding a session, and an
   *  SVG loaded through `<img>` cannot execute what an inlined one could. */
  qr: string;
};

/**
 * A request carrying the session, not just the publishable key.
 *
 * Its own function rather than a flag on `auth`, because forgetting the bearer
 * on a factor call does not fail loudly — it fails as "no factors", which
 * reads as "you have not enrolled" and sends somebody to enrol a second time.
 */
async function authed<T>(path: string, body?: unknown): Promise<T> {
  if (!configured()) throw new AuthError('Sign-in is not configured.', 'not_configured');
  const token = await accessToken();
  if (!token) throw new AuthError('Your session has ended. Sign in again.', 'no_session');

  const res = await fetch(`${SUPABASE_URL}/auth/v1/${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      apikey: PUBLISHABLE_KEY,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    const code = String(detail.error_code ?? detail.error ?? res.status);
    throw new AuthError(factorMessage(code, res.status), code);
  }
  return res.json() as Promise<T>;
}

/**
 * What to say when a factor call fails.
 *
 * Unlike sign-in, these messages may be specific: the person is already
 * authenticated, so telling them their code was wrong reveals nothing to
 * anybody who is not already holding their session.
 */
function factorMessage(code: string, status: number): string {
  if (code.includes('invalid') || status === 422 || status === 400)
    return 'That code was not accepted. Codes last about thirty seconds — wait for the next one and try again.';
  if (status === 429) return 'Too many attempts. Wait a minute before trying again.';
  if (status === 401) return 'Your session has ended. Sign in again and restart this.';
  return 'The second factor could not be set up right now. This is not a problem with your code.';
}

/** The assurance level this session actually holds, read from its own token.
 *
 *  Not a claim about what the account requires — the API decides that and
 *  deliberately will not say which refusal it made. This only answers "did I
 *  present a second factor on this session", which the token itself carries
 *  and the browser is entitled to read. */
export function assuranceLevel(): 'aal1' | 'aal2' | null {
  if (!session) return null;
  try {
    // A JWT is three dot-separated parts; the middle one is the payload. A
    // token that is not shaped like one has no `aal` to read, and indexing
    // past the end of the split is exactly how that becomes a crash.
    const [, encoded] = session.accessToken.split('.');
    if (!encoded) return null;
    const payload = JSON.parse(atob(encoded.replace(/-/g, '+').replace(/_/g, '/')));
    return payload.aal === 'aal2' ? 'aal2' : 'aal1';
  } catch {
    // A token this cannot parse is not a reason to lock somebody out of a
    // screen; the API is the authority and will refuse if it must.
    return null;
  }
}

/**
 * Does this account already carry a factor it can present?
 *
 * Used to decide whether a fresh `aal1` session should go and satisfy one
 * before trying the product. Never a claim about whether the account *needs*
 * one — the API decides that and deliberately will not say. This only answers
 * "is there something to present", which changes a dead end into a step.
 *
 * A failure here is not a reason to block sign-in: the answer is only used to
 * offer a shortcut, and an account that cannot be asked simply does not get
 * one.
 */
export async function hasVerifiedFactor(): Promise<boolean> {
  try {
    return (await listFactors()).some((f) => f.status === 'verified');
  } catch {
    return false;
  }
}

/** Every factor on the account, enrolled or half-enrolled. */
export async function listFactors(): Promise<Factor[]> {
  const user = await authed<{ factors?: Factor[] }>('user');
  return user.factors ?? [];
}

/**
 * Begin enrolment. Returns the secret to be shown once.
 *
 * An unverified factor is left behind if somebody abandons this, and Supabase
 * refuses a second factor with the same friendly name — so the name carries
 * the moment it was created, and `enrolTotp` clears any unverified leftovers
 * first. Otherwise a person who closes the tab can never enrol again.
 */
export async function enrolTotp(): Promise<Enrolment> {
  for (const stale of await listFactors()) {
    if (stale.status !== 'verified') await unenrol(stale.id);
  }
  const created = await authed<{
    id: string;
    totp: { qr_code: string; secret: string; uri: string };
  }>('factors', {
    factor_type: 'totp',
    friendly_name: `Premise Sense ${new Date().toISOString().slice(0, 16)}`,
  });
  return {
    factorId: created.id,
    secret: created.totp.secret,
    uri: created.totp.uri,
    // Ours, from the URI — not the provider's picture. See `qrFromUri`.
    qr: qrFromUri(created.totp.uri) ?? imageSource(created.totp.qr_code),
  };
}

/**
 * Draw the QR ourselves, from the `otpauth://` URI.
 *
 * Supabase also returns a picture, and twice now it has not been in the format
 * the code expected — first a raw `<svg>` where a data URI was assumed, then
 * something that still would not load. Each guess produced the same thing on
 * screen: a white box with a broken image, on the one screen where the
 * picture is the entire point.
 *
 * The URI is the actual secret material and its format is a specification
 * rather than a vendor's choice, so encoding it here removes the provider's
 * picture from the path entirely. `qrcode-generator` is ~10KB, has no
 * dependencies, runs in the browser and makes no network request — the QR
 * encodes a shared secret, and sending that to a third-party image service to
 * be rendered would be an extraordinary thing to do.
 *
 * The provider's picture stays as a fallback for the case where a URI is
 * absent, because a fallback that has failed twice is still better than none.
 */
function qrFromUri(uri: string): string | null {
  if (!uri) return null;
  try {
    // 0 picks the smallest version that fits; 'M' is 15% error correction,
    // which survives a phone camera at an angle.
    const code = qrcode(0, 'M');
    code.addData(uri);
    code.make();
    // 6px a module with a 2-module quiet zone. Scanners need the quiet zone;
    // without it a QR against a dark panel is often simply not seen.
    return code.createDataURL(6, 2);
  } catch {
    return null;
  }
}

/**
 * Turn whatever the provider calls a QR code into something `<img>` can load.
 *
 * Supabase returns `totp.qr_code` as a **raw SVG document** — `<svg …>` — not
 * a data URI. Putting that straight into `src` gives the browser a string
 * beginning with a `<`, and the enrolment screen shows a broken image with the
 * one thing somebody needs on it.
 *
 * Wrapping rather than inlining keeps the safety property that made `<img>`
 * the right element: markup dropped into the document could carry script, and
 * an SVG loaded through `src` cannot.
 */
function imageSource(qr: string): string {
  const value = qr.trim();
  if (value.startsWith('data:')) return value;
  if (value.startsWith('<svg')) return `data:image/svg+xml;utf8,${encodeURIComponent(value)}`;
  // Some deployments answer with a bare base64 payload. Anything else is
  // handed through untouched — a broken image beside a working secret is
  // recoverable, and guessing at an unknown format is not.
  if (/^[A-Za-z0-9+/=]+$/.test(value) && value.length > 100)
    return `data:image/png;base64,${value}`;
  return value;
}

/** Remove a factor. Used for abandoned enrolments, never offered as a way to
 *  turn the requirement off — the API decides that, not this screen. */
export async function unenrol(factorId: string): Promise<void> {
  const token = await accessToken();
  await fetch(`${SUPABASE_URL}/auth/v1/factors/${factorId}`, {
    method: 'DELETE',
    headers: { apikey: PUBLISHABLE_KEY, Authorization: `Bearer ${token}` },
  });
}

/**
 * Present the code, and adopt the session it returns.
 *
 * The point of the whole flow is this exchange: verifying returns a *new*
 * access token whose `aal` is `aal2`, and adopting it is what makes the
 * product usable. Verifying and keeping the old token would satisfy the
 * provider and change nothing about what the API sees, which is the mistake
 * this comment exists to prevent.
 */
export async function verifyTotp(factorId: string, code: string): Promise<Session> {
  const challenge = await authed<{ id: string }>(`factors/${factorId}/challenge`, {});
  return adopt(
    await authed<TokenResponse>(`factors/${factorId}/verify`, {
      challenge_id: challenge.id,
      code: code.replace(/\s/g, ''),
    }),
  );
}

/**
 * Exchange an emailed link's token hash for a session.
 *
 * The token-hash flow — Supabase's equivalent of PKCE for a link the browser
 * did not start. The link carries only a hash of a one-time token, never a
 * session, and it is spent here when the person presses a button: no session
 * is ever in an address, and a mail scanner that opens the link spends nothing.
 */
export async function exchangeLink(tokenHash: string, type: string): Promise<Session> {
  try {
    const token = await auth<TokenResponse>('verify', { type, token_hash: tokenHash });
    return adopt({ ...token, user: { email: token.user?.email ?? emailOf(token.access_token) } });
  } catch (e) {
    if (e instanceof AuthError && e.code === 'not_configured') throw e;
    throw new AuthError('That link has expired or has already been used.', 'link_expired');
  }
}

/** A minute short of the API's ten, so a session this thinks is recent is not
 *  refused a moment later. */
const RECONFIRM_WINDOW_MS = 9 * 60_000;

/**
 * Has this session proved its holder recently enough for a consequential
 * change? Read from the token's own `amr` claim; the API is the authority and
 * refuses with `reauthentication_required` whatever this says.
 *
 * Owner and Admin re-confirm with their authenticator code, everyone else with
 * their password — the same rule as `strata_core.identity.recently_confirmed`.
 */
export function recentlyConfirmed(secondFactor: boolean): boolean {
  if (!session) return false;
  let raw: unknown;
  try {
    const [, encoded] = session.accessToken.split('.');
    if (!encoded) return false;
    raw = JSON.parse(atob(encoded.replace(/-/g, '+').replace(/_/g, '/'))).amr;
  } catch {
    return false;
  }
  if (!Array.isArray(raw)) return false;
  const wanted = secondFactor ? ['totp'] : ['password', 'totp'];
  const now = Date.now();
  return raw.some((entry: { method?: unknown; timestamp?: unknown }) => {
    if (typeof entry?.method !== 'string' || typeof entry.timestamp !== 'number') return false;
    const at = entry.timestamp * 1000;
    return wanted.includes(entry.method) && now - at < RECONFIRM_WINDOW_MS && at - now < 60_000;
  });
}

/**
 * Prove it is you again, on this session.
 *
 * With a code, the verified factor is challenged and the session it returns —
 * still aal2, with a fresh `totp` time — is adopted. With a password, a new
 * sign-in; for a role without a second factor that is the same assurance the
 * session already had.
 */
export async function reconfirm(proof: { password: string } | { code: string }): Promise<void> {
  if ('code' in proof) {
    const factor = (await listFactors()).find(
      (f) => f.status === 'verified' && f.factor_type === 'totp',
    );
    if (!factor)
      throw new AuthError(
        'This account has no authenticator set up. Set one up from the second-factor page first.',
        'no_factor',
      );
    await verifyTotp(factor.id, proof.code);
    return;
  }
  const email = session?.email;
  if (!email) throw new AuthError('Your session has ended. Sign in again.', 'no_session');
  try {
    const token = await auth<TokenResponse>('token?grant_type=password', {
      email,
      password: proof.password,
    });
    adopt({ ...token, user: { email } });
  } catch (e) {
    if (e instanceof AuthError && e.status === 400)
      throw new AuthError('That password was not accepted.', 'reconfirm_failed', 400);
    throw e;
  }
}

export async function accessToken(): Promise<string | null> {
  if (session && session.expiresAt > Date.now()) return session.accessToken;
  refreshing ??= refresh().finally(() => {
    refreshing = null;
  });
  return (await refreshing)?.accessToken ?? null;
}

export function current(): Session | null {
  return session;
}

export function signOut(): void {
  session = null;
  try {
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ORG_KEY);
  } catch {
    /* nothing stored, nothing to clear */
  }
}

/** Which organization the session is acting as.
 *
 *  Not a credential: the API re-checks membership on every request and refuses
 *  an organization the subject does not hold. It is persisted so that choosing
 *  once survives a reload. */
export function organization(): string | null {
  try {
    return localStorage.getItem(ORG_KEY);
  } catch {
    return null;
  }
}

export function chooseOrganization(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(ORG_KEY);
    else localStorage.setItem(ORG_KEY, id);
  } catch {
    /* the session still works; the choice just will not survive a reload */
  }
}

/** True when sign-in can work at all in this build. The Login screen renders a
 *  configuration message rather than a form when it cannot, because a form
 *  that cannot succeed teaches people their password is wrong. */
export const authConfigured = configured;
